import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { motion, AnimatePresence } from 'framer-motion';
import { useAdmin } from '../lib/AdminContext';
import DynamicForm from './DynamicForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useEditorContext } from '../lib/EditorContext';

const { FiEdit3, FiX, FiSave } = FiIcons;

export default function OnSiteEditor() {
  const { isAdmin } = useAdmin();
  const { editors } = useEditorContext();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTabId, setActiveTabId] = useState(null);

  const [contentStr, setContentStr] = useState('');
  const [parsedObj, setParsedObj] = useState(null);

  const [editorMode, setEditorMode] = useState('visual'); // 'visual' | 'code'
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [error, setError] = useState(null);

  const editorKeys = Object.keys(editors);

  useEffect(() => {
    if (isOpen && editorKeys.length > 0) {
      if (!activeTabId || !editors[activeTabId]) {
        setActiveTabId(editorKeys[0]);
      }
    }
  }, [isOpen, editors, activeTabId, editorKeys]);

  useEffect(() => {
    if (activeTabId && editors[activeTabId]) {
      const currentContent = editors[activeTabId].content;
      setContentStr(JSON.stringify(currentContent, null, 2));
      setParsedObj(currentContent);
      setError(null);
    }
  }, [activeTabId, editors]);

  if (!isAdmin) return null;

  const handleVisualChange = (newObj) => {
    setParsedObj(newObj);
    setContentStr(JSON.stringify(newObj, null, 2));
    setError(null);
  };

  const handleCodeChange = (e) => {
    const val = e.target.value;
    setContentStr(val);
    try {
      setParsedObj(JSON.parse(val));
      setError(null);
    } catch (err) {
      // JSON is currently invalid while typing, just ignore
    }
  };

  const handleSave = async () => {
    if (!activeTabId || !editors[activeTabId]) return;

    setIsSaving(true);
    setSaveStatus('');
    setError(null);
    try {
      let payloadToSave = parsedObj;

      if (editorMode === 'code') {
        try {
          payloadToSave = JSON.parse(contentStr);
        } catch (e) {
          setError('Invalid JSON format. Please fix it before saving.');
          setIsSaving(false);
          return;
        }
      }

      const { error } = await supabase
        .from('page_content')
        .upsert({ page_id: activeTabId, content: payloadToSave });

      if (error) throw error;
      setSaveStatus('Saved!');

      const onSaveSuccess = editors[activeTabId].onSave;
      if (onSaveSuccess) onSaveSuccess(payloadToSave);

      setTimeout(() => {
        setSaveStatus('');
        setIsOpen(false);
      }, 1500);

    } catch (err) {
      console.error('Error saving content:', err);
      setError('Failed to save content.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className={`fixed z-50 p-4 bg-brand-rose text-white rounded-full shadow-[4px_4px_0px_0px_rgba(45,55,72,1)] border-2 border-brand-darkGray flex items-center justify-center hover:translate-y-1 hover:shadow-[2px_2px_0px_0px_rgba(45,55,72,1)] transition-all bottom-6 right-6`}
        title="Edit Page Content"
      >
        <SafeIcon icon={FiEdit3} className="w-6 h-6" />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]"
            />

            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 right-0 h-full w-full max-w-xl bg-white dark:bg-brand-slate shadow-2xl z-[70] flex flex-col border-l border-sand-200"
            >
              <div className="flex items-center justify-between p-6 border-b border-sand-200 bg-sand-50 dark:bg-brand-slate">
                <h2 className="text-xl font-bold text-[#333] dark:text-[#fff]">Edit Content</h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>

              {editorKeys.length > 1 && (
                <div className="flex bg-sand-50 dark:bg-brand-slate border-b border-sand-200 p-2 gap-2 overflow-x-auto">
                  {editorKeys.map((key) => (
                    <button
                      key={key}
                      onClick={() => setActiveTabId(key)}
                      className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap ${
                        activeTabId === key
                          ? 'bg-brand-rose text-white'
                          : 'bg-white dark:bg-brand-slate/80 text-gray-600 dark:text-gray-300 hover:bg-sand-100 dark:hover:bg-brand-slate'
                      }`}
                    >
                      {editors[key].label || key}
                    </button>
                  ))}
                </div>
              )}

              <div className="p-4 border-b border-sand-200 bg-white dark:bg-brand-slate flex justify-between items-center">
                <div className="flex bg-sand-100 dark:bg-brand-slate/50 p-1 rounded-lg">
                  <button
                    onClick={() => setEditorMode('visual')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${editorMode === 'visual' ? 'bg-white shadow-sm text-brand-rose' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'}`}
                  >
                    Visual
                  </button>
                  <button
                    onClick={() => setEditorMode('code')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${editorMode === 'code' ? 'bg-white shadow-sm text-brand-rose' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'}`}
                  >
                    Code
                  </button>
                </div>

                {error && <span className="text-red-500 text-sm font-medium truncate ml-4" title={error}>{error}</span>}
                {saveStatus && <span className="text-green-500 text-sm font-medium">{saveStatus}</span>}
              </div>

              <div className="flex-1 overflow-y-auto p-6 bg-sand-50/50 dark:bg-brand-slate/80 custom-scrollbar">
                {editorMode === 'visual' ? (
                  parsedObj ? (
                    <DynamicForm data={parsedObj} onChange={handleVisualChange} />
                  ) : (
                    <p className="text-center text-gray-500 py-10">Invalid or empty content structure.</p>
                  )
                ) : (
                  <textarea
                    value={contentStr}
                    onChange={handleCodeChange}
                    className="w-full h-full min-h-[400px] p-4 font-mono text-sm border border-sand-200 rounded-xl bg-gray-900 text-green-400 focus:ring-2 focus:ring-brand-rose outline-none resize-none"
                    spellCheck={false}
                  />
                )}
              </div>

              <div className="p-6 border-t border-sand-200 bg-white dark:bg-brand-slate flex justify-end gap-4">
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-6 py-2.5 rounded-full font-medium text-[#333] dark:text-[#fff] bg-sand-100 hover:bg-sand-200 dark:bg-brand-slate dark:hover:bg-brand-slate/80 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving || !activeTabId}
                  className="px-6 py-2.5 bg-brand-rose text-white rounded-full font-medium shadow-[2px_2px_0px_0px_rgba(45,55,72,1)] border-2 border-brand-darkGray hover:translate-y-0.5 hover:shadow-[1px_1px_0px_0px_rgba(45,55,72,1)] transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  <SafeIcon icon={FiSave} />
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
